import { HTMLAttributes, useEffect, useState } from 'react'
import { z } from 'zod'
import { AxiosError } from 'axios'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useRouter, useSearch } from '@tanstack/react-router'
import { AuthService } from '@/services/auth.service'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { selectRefreshAuth, useAuthStore } from '@/stores/auth.store'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { PasswordInput } from '@/components/password-input'

type ResetPasswordFormProps = HTMLAttributes<HTMLDivElement>

const formSchema = z
  .object({
    tempPassword: z
      .string()
      .min(1, { message: 'Por favor, digite a senha temporária' }),
    password: z
      .string()
      .min(1, { message: 'Por favor, digite sua nova senha' })
      .min(7, { message: 'A senha deve ter pelo menos 7 caracteres' }),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'As senhas não coincidem.',
    path: ['confirmPassword'],
  })

export function ResetPasswordForm({
  className,
  ...props
}: ResetPasswordFormProps) {
  const [isLoading, setIsLoading] = useState(false)
  const refreshAuth = useAuthStore(selectRefreshAuth)
  const router = useRouter()
  const { email } = useSearch({ from: '/(auth)/reset-password' })

  useEffect(() => {
    if (!email) {
      router.navigate({ to: '/sign-in' })
    }
  }, [email, router])

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      tempPassword: '',
      password: '',
      confirmPassword: '',
    },
  })

  async function onSubmit(data: z.infer<typeof formSchema>) {
    setIsLoading(true)

    try {
      const tokens = await AuthService.resetPassword({
        email: email as string,
        tempPassword: data.tempPassword,
        password: data.password,
      })
      refreshAuth(tokens)
      router.navigate({ to: '/' })
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error?.status === 401) {
          toast.error('Senha temporária inválida.', {
            description:
              'Verifique a senha recebida por email e tente novamente.',
          })
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (!email) return null

  return (
    <div className={cn('grid gap-6', className)} {...props}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className='grid gap-2'>
            <FormField
              control={form.control}
              name='tempPassword'
              render={({ field }) => (
                <FormItem className='space-y-1'>
                  <FormLabel>Senha Temporária</FormLabel>
                  <FormControl>
                    <PasswordInput placeholder='********' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='password'
              render={({ field }) => (
                <FormItem className='space-y-1'>
                  <FormLabel>Nova Senha</FormLabel>
                  <FormControl>
                    <PasswordInput placeholder='********' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='confirmPassword'
              render={({ field }) => (
                <FormItem className='space-y-1'>
                  <FormLabel>Confirme a Nova Senha</FormLabel>
                  <FormControl>
                    <PasswordInput placeholder='********' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button className='mt-2' disabled={isLoading}>
              {isLoading && <Loader2 className='mr-2 h-5 w-5 animate-spin' />}
              Redefinir Senha
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
